import React, { Component } from 'react';
import PropTypes from 'prop-types';
//Opentok
import { OTSubscriber } from 'opentok-react';
import {
    Doctor,
    LevelTwoAdmin,
    Patient,
    toDisplayName,
} from '../../../../utils/common/NamingTerms';
import ScreenShotEditModal from './ScreenShotEditModal';
import { SubscriberHosts, SubscriberSlot } from './SubscriberSlot';
import { emitLiveCaption, emitStreamId } from '../../../action/SocketActions';
import store from '../../../../sessionStorage';

let width = window.innerWidth;
// let height = window.innerHeight;
let isMobileView = false;
if (width <= 768) {
    isMobileView = true;
}
if (!isMobileView) {
    width = (width / 100) * 65;
}

const visitType = store.get('VISIT_TYPE');
const sessionType = sessionStorage.getItem('SESSION_TYPE');
const isInPersonOneToOne = visitType === 'one_to_one' && sessionType === 'IN_PERSON';

// Each stream keeps a single subscriber for its whole lifetime, even when its
// tile moves between the full-size and the small overlay layout, so only the
// options the SDK can change on a live subscriber may differ between the two.
// Size comes from the tile the subscriber is placed in.
const subscriberOptionsSmallVideo = {
    showControls: true,
    subscribeToVideo: !isInPersonOneToOne,
};

let subscriber;
/**
 * @package VirtuOwl
 * @type {React.ComponentClass}
 */
class Subscribers extends Component {
    static propTypes = {
        streams: PropTypes.array,
        sessionHelper: PropTypes.any,
    };
    constructor(props) {
        super(props);
        this.state = {
            subscriber: null,
            screenShare: false,
            caption: '',
            emittedStreamIds: new Set(),
        };
        
        // Track per-stream caption timeouts to prevent emission leaks
        this.captionTimeouts = new Map();
        
        // Track last emitted caption per stream to prevent duplicate emissions
        this.lastEmittedCaptions = new Map();

        // One persistent host element per stream - see SubscriberSlot.
        this.subscriberHosts = new SubscriberHosts();
        // Streams placed by the current render, and their OTSubscriber refs.
        this.placedStreams = new Map();
        this.otSubscriberRefs = new Map();
        this.subscriberNameModes = new Map();

        this.subscriberEventHandlers = {
            captionReceived: (event) => {
                const { streams } = this.props;
                const urlParams = new URLSearchParams(window.location.search);

                // Find the stream that matches event.streamId
                const matchedStream = streams.find(
                    (stream) => (stream.streamId || stream.id) === event.streamId
                );
                const resolvedUserId = this.resolveStreamUserId(matchedStream);

                const userInfo = {
                    caption: event.caption,
                    sessionId: this.props.visitId || urlParams.get('visitId'),
                    streamId: event.streamId,
                    isFinal: event.isFinal !== false,
                    userId: resolvedUserId,
                };
                
                // Only emit if we have a valid userId
                if (!userInfo.userId) {
                    console.warn('Subscribers: Skipping caption emit - no valid userId found for stream:', {
                        streamId: event.streamId,
                        streamName: matchedStream?.name,
                        availableUsers: {
                            sessionUsers: this.props.studentDetails?.sessionUsers?.map(u => u.name),
                            externalUsers: this.props.studentDetails?.externalUsersDetails?.map(u => u.name)
                        }
                    });
                    return;
                }
                
                // Deduplicate: skip if this exact caption was already emitted for this stream
                const lastCaption = this.lastEmittedCaptions.get(event.streamId);
                if (lastCaption === event.caption && event.isFinal) {
                    // console.log('Subscribers: Skipping duplicate caption for stream:', event.streamId);
                    return;
                }
                
                // Note: streamId mappings are emitted in componentDidUpdate, not here
                
                // Clear existing timeout for this stream to prevent multiple emissions
                if (this.captionTimeouts.has(event.streamId)) {
                    clearTimeout(this.captionTimeouts.get(event.streamId));
                }
                
                // Set new timeout and store reference
                const timeoutId = setTimeout(() => {
                    emitLiveCaption(userInfo);
                    // Track this caption as emitted
                    this.lastEmittedCaptions.set(event.streamId, event.caption);
                    // Clean up timeout reference
                    this.captionTimeouts.delete(event.streamId);
                }, 2000);
                
                this.captionTimeouts.set(event.streamId, timeoutId);
                this.setState({ caption: event.caption });
            },
            videoEnabled: (videoEnabled) => {
                // Repaint in place when video comes back. Never remount the
                // subscriber here - that would interrupt the audio.
                this.forceUpdate();
            },
            videoDisabled: (videoDisabled) => {
                // Camera turned off, or the SDK dropped video to protect audio
                // while a screen share is using the bandwidth. Repaint only;
                // the audio subscription stays alive.
                this.forceUpdate();
            },
            audioEnabled: (audioEnabled) => {
                // Do not force update on audio changes
            },
        };
    }

    componentDidMount() {
        const { sessionHelper } = this.props;

        // NEW: Listen for requests to re-emit streamId mappings for all remote streams.
        // This complements the Publisher's listener - when someone (re)joins and requests
        // mappings, we re-process all remote streams we're currently receiving.
        sessionHelper.session.on('signal:requestStreamIds', (event) => {
            if (
                event.from &&
                sessionHelper.session.connection &&
                event.from.connectionId === sessionHelper.session.connection.connectionId
            ) {
                return; // Ignore our own request
            }
            // console.log('[Subscribers] Received requestStreamIds signal - re-emitting remote stream mappings');
            this.logStreamAndUserId();
        });
    }

    componentWillUnmount() {
        // Clear all pending caption timeouts to prevent memory leaks
        this.captionTimeouts.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        this.captionTimeouts.clear();
        this.lastEmittedCaptions.clear();

        this.subscriberHosts.clear();
    }

    componentDidUpdate(prevProps) {
        this.subscriberHosts.prune(new Set((this.props.streams || []).map((s) => s && s.id)));
        this.syncSubscriberStyles();

        const streamsChanged = this.props.streams !== prevProps.streams;
        const streamsLengthChanged = this.props.streams?.length !== prevProps.streams?.length;

        const currentSessionUsers = this.props.studentDetails?.sessionUsers;
        const prevSessionUsers = prevProps.studentDetails?.sessionUsers;
        const sessionUsersBecameAvailable =
            !prevSessionUsers && currentSessionUsers && this.props.streams?.length;
        
        // Detect sessionUsers content changes (user added/removed/rejoined)
        const sessionUsersContentChanged =
            prevSessionUsers &&
            currentSessionUsers &&
            JSON.stringify(prevSessionUsers.map(u => u.userId).sort()) !==
                JSON.stringify(currentSessionUsers.map(u => u.userId).sort());
        
        // Detect externalUsersDetails content changes
        const currentExternalUsers = this.props.studentDetails?.externalUsersDetails;
        const prevExternalUsers = prevProps.studentDetails?.externalUsersDetails;
        const externalUsersContentChanged =
            JSON.stringify(prevExternalUsers?.map(u => u.userId).sort()) !==
                JSON.stringify(currentExternalUsers?.map(u => u.userId).sort());

        // Emit streamIds if any change detected
        const shouldEmitStreamIds = streamsChanged || streamsLengthChanged || sessionUsersContentChanged || externalUsersContentChanged || sessionUsersBecameAvailable;
        
        if (shouldEmitStreamIds) {
            // console.log('[Subscribers] componentDidUpdate detected changes - re-emitting streamIds:', {
            //     streamsChanged,
            //     streamsLengthChanged,
            //     prevStreamsLength: prevProps.streams?.length,
            //     currentStreamsLength: this.props.streams?.length,
            //     sessionUsersContentChanged,
            //     externalUsersContentChanged,
            //     sessionUsersBecameAvailable
            // });
            this.logStreamAndUserId();
        }
    }

    getCurrentUserId = () => {
        const userSession = store.get('userSession');
        const insAdminSession = store.get('insAdminSession');
        const urlParams = new URLSearchParams(window.location.search);
        const urlUserId = urlParams.get('userId');

        if ((this.props.hasUrlParams || urlParams.get('socketAuthentication')) && urlUserId) {
            return urlUserId;
        }

        return (
            store.get('bloomIfAI_id') ||
            userSession?.id ||
            insAdminSession?.id ||
            urlUserId ||
            this.props.userId ||
            null
        );
    };

    isValidUserId = (userId) => userId && userId !== 'undefined' && userId !== 'null';

    resolveStreamUserId = (stream) => {
        const myUserId = this.getCurrentUserId();
        const studentDetails = this.props.studentDetails;

        let matchedUser = studentDetails?.sessionUsers?.find((user) => user.name === stream?.name);
        if (!matchedUser && studentDetails?.externalUsersDetails) {
            matchedUser = studentDetails.externalUsersDetails.find(
                (user) => user.name === stream?.name
            );
        }

        if (this.isValidUserId(matchedUser?.userId) && matchedUser.userId !== myUserId) {
            return matchedUser.userId;
        }

        return this.resolveTargetUserId();
    };

    resolveTargetUserId = () => {
        const myUserId = this.getCurrentUserId();
        const urlParams = new URLSearchParams(window.location.search);
        
        // Prefer sessionUsers/externalUsersDetails when available; one-to-one
        // Subscriber can mount without studentDetails, so fall back to call ids.
        const studentDetails = this.props.studentDetails;
        
        // Count total users in session (excluding self)
        const otherSessionUsers = studentDetails?.sessionUsers?.filter(u => u.userId !== myUserId) || [];
        const otherExternalUsers = studentDetails?.externalUsersDetails?.filter(u => u.userId !== myUserId) || [];
        const totalOtherUsers = otherSessionUsers.length + otherExternalUsers.length;
        
        // If 2+ other users (3+ total), we CAN'T guess which one is the target
        // This prevents returning wrong userId in multi-user sessions
        if (totalOtherUsers >= 2) {
            console.warn('Subscribers: Cannot resolve target userId in multi-user session (3+ users):', {
                totalUsers: totalOtherUsers + 1,
                sessionUsers: otherSessionUsers.length,
                externalUsers: otherExternalUsers.length
            });
            return null;
        }
        
        // If exactly 1 other user, return their userId (1-to-1 session)
        if (totalOtherUsers === 1) {
            const targetUser = otherSessionUsers[0] || otherExternalUsers[0];
            return targetUser?.userId || null;
        }

        const oneToOneCandidates = [
            this.props.callpatientUserId,
            this.props.calldoctorUserId,
            urlParams.get('createdByUserId'),
        ];
        const targetUserId = oneToOneCandidates.find(
            (userId) => this.isValidUserId(userId) && userId !== myUserId
        );

        if (targetUserId) {
            return targetUserId;
        }

        // Last resort: use createdByUserId from URL if it's not current user (initial join case)
        const createdByUserId = urlParams.get('createdByUserId');
        if (createdByUserId && createdByUserId !== myUserId) {
            return createdByUserId;
        }

        // Return null if no valid target found
        console.warn('Subscribers: Could not resolve target userId from session data');
        return null;
    };

    logStreamAndUserId = () => {
        const { streams, studentDetails } = this.props;

        // Count total users (regular + anonymous) excluding self
        const myUserId = this.getCurrentUserId();
        const sessionUsersCount = studentDetails?.sessionUsers?.filter(u => u.userId !== myUserId).length || 0;
        const externalUsersCount = studentDetails?.externalUsersDetails?.length || 0;
        const targetUserId = this.resolveTargetUserId();
        const hasOtherUsers = (sessionUsersCount + externalUsersCount) > 0 || !!targetUserId;
        
        if (!hasOtherUsers) {
            // console.log('[Subscribers] logStreamAndUserId: No other users detected, skipping emit');
            return; // Don't emit if no other users
        }

        if (!streams || streams.length === 0) {
            // console.log('[Subscribers] logStreamAndUserId: No streams available, skipping emit');
            return; // Wait for proper data
        }

        // console.log('[Subscribers] logStreamAndUserId: Processing streams');
        // console.log('  📊 STREAM COUNT:', streams.length);
        // console.log('  👥 SESSION USERS:', sessionUsersCount, '| EXTERNAL USERS:', externalUsersCount);
        // console.log('  🎥 STREAMS:');
        // streams.forEach((s) => {
        //     // console.log('    -', s.id, '→', s.name);
        // });
        // // console.log('  👤 SESSION USERS:');
        // if (studentDetails?.sessionUsers) {
        //     studentDetails.sessionUsers.forEach((u) => {
        //         // console.log('    -', u.userId, '→', u.name);
        //     });
        // }
        // // console.log('  🌐 EXTERNAL USERS:');
        // if (studentDetails?.externalUsersDetails) {
        //     studentDetails.externalUsersDetails.forEach((u) => {
        //         // console.log('    -', u.userId, '→', u.name);
        //     });
        // }

        let unmatchedStreams = [];

        streams.forEach((stream) => {
            const resolvedUserId = this.resolveStreamUserId(stream);

            if (resolvedUserId) {
                // Always use stream.id from the loop iteration
                const data = { streamId: stream.id, userId: resolvedUserId };
                emitStreamId(data);
                
                // console.log('[Subscribers] ✅ Emitted streamId mapping:', {
                //     streamId: stream.id,
                //     streamName: stream.name,
                //     userId: matchedUser.userId,
                //     userName: matchedUser.name
                // });
            } else {
                unmatchedStreams.push({ streamId: stream.id, streamName: stream.name });
                console.warn('[Subscribers] ❌ STREAM NOT MATCHED:', {
                    streamId: stream.id,
                    streamName: stream.name,
                    availableUsers: {
                        sessionUsers: studentDetails?.sessionUsers?.map(u => u.name),
                        externalUsers: studentDetails?.externalUsersDetails?.map(u => u.name)
                    }
                });
            }
        });

        // console.log('[Subscribers] logStreamAndUserId: Summary', {
        //     totalStreams: streams.length,
        //     unmatchedStreams: unmatchedStreams.length > 0 ? unmatchedStreams : 'none'
        // });
    };

    //onScreenShot
    onScreenShot() {
        if (
            subscriber !== null &&
            typeof subscriber !== 'undefined' &&
            typeof subscriber.getImgData() !== 'undefined'
        ) {
            return subscriber.getImgData();
        }
    }

    //onscreenShare
    onscreenShare = (onScreenShare, callback) => {
        this.setState({ screenShare: onScreenShare });
    };

    // Places a stream's video in the current layout. The OTSubscriber itself is
    // rendered by renderSubscriberPortals(), so moving the tile between layouts
    // (screen share, small overlay, whiteboard) never remounts it - each remount
    // tore down and rebuilt the audio subscription, which was heard as the
    // speaker's voice breaking up.
    renderSubscriber = (stream, isSmallVideo = false) => {
        this.placedStreams.set(stream.id, { stream, isSmallVideo });
        return <SubscriberSlot host={this.subscriberHosts.get(stream.id)} />;
    };

    // Rendered last and always at the same place in the tree, after every
    // renderSubscriber() call of this render.
    renderSubscriberPortals = () =>
        Array.from(this.placedStreams.values()).map(({ stream, isSmallVideo }) => {
            // Key ONLY by stream id. Keying on hasVideo remounted the subscriber
            // every time the camera went on or off - including the automatic
            // video drop the SDK performs while a screen share is saturating the
            // link. Camera on/off is repainted by the videoEnabled/videoDisabled
            // handlers instead.
            const subscriberKey = stream.id;

            // Always subscribe with the live OpenTok Stream object. render()
            // shallow-clones streams to relabel them, and the SDK reads
            // stream.hasVideo off whatever object it was given - a clone keeps the
            // value from join time, so a camera that starts after the subscribe
            // stays black until something remounts the tile.
            // The relabelled name reaches the overlay via the `name` property.
            const liveStream = this.props.streams?.find((s) => s && s.id === stream.id) || stream;
            const baseProperties = isSmallVideo ? subscriberOptionsSmallVideo : {};

            return this.subscriberHosts.portal(
                stream.id,
                <OTSubscriber
                    key={subscriberKey}
                    ref={(ref) => {
                        this.OTSubscriber = ref;
                        if (ref) {
                            this.otSubscriberRefs.set(stream.id, ref);
                        }
                    }}
                    session={this.props.sessionHelper.session}
                    stream={liveStream}
                    eventHandlers={this.subscriberEventHandlers}
                    onGetSubscriber={(sub) => {
                        subscriber = sub;
                        subscriber.subscribeToCaptions(true).catch((err) => {
                            console.warn(err);
                        });
                    }}
                    properties={{
                        ...baseProperties,
                        name: stream.name,
                        width: '100%',
                        height: '100%',
                        style: { nameDisplayMode: isSmallVideo ? 'off' : 'auto' },
                    }}
                    style={{ width: '100%', height: '100%' }}
                />
            );
        });

    // The name overlay is off on the small overlay tile. Style is fixed when a
    // subscriber is created, so update it in place as a tile changes size.
    syncSubscriberStyles = () => {
        // Forget subscribers that are no longer placed (their OTSubscriber
        // has unmounted).
        [this.otSubscriberRefs, this.subscriberNameModes].forEach((byStream) => {
            Array.from(byStream.keys()).forEach((streamId) => {
                if (!this.placedStreams.has(streamId)) {
                    byStream.delete(streamId);
                }
            });
        });

        this.placedStreams.forEach(({ isSmallVideo }, streamId) => {
            const sub = this.otSubscriberRefs.get(streamId)?.getSubscriber?.();
            const nameDisplayMode = isSmallVideo ? 'off' : 'auto';
            if (
                sub &&
                typeof sub.setStyle === 'function' &&
                this.subscriberNameModes.get(streamId) !== nameDisplayMode
            ) {
                sub.setStyle('nameDisplayMode', nameDisplayMode);
                this.subscriberNameModes.set(streamId, nameDisplayMode);
            }
        });
    };

    render() {
        let { streams } = { ...this.props };
        // Update stream names at the start of render
        streams = streams.map((stream) => ({
            ...stream,
            name: toDisplayName(stream.name),
        }));

        const containerWidth = `${100 / Math.ceil(Math.sqrt(streams.length))}%`;
        const containerHeight = `${
            100 / Math.ceil(streams.length / Math.ceil(Math.sqrt(streams.length)))
        }%`;
        let isScreenShared = false;
        isScreenShared = streams.some((i) => i.name === 'screen');

        const shouldHideSmallVideo =
            isScreenShared &&
            this.props.fullScreenEnabled &&
            (this.props.isOneToOne || this.props.sessionTypeParams);

        // Track rendered streams to avoid duplicates
        const renderedStreams = new Set();
        this.placedStreams = new Map();

        return (
            <div className="parent-div">
                {this.props.renderLogoWithWifi()}
                <div
                    className="classroom-video classroom-user"
                    style={{ backgroundColor: 'black' }}
                >
                    {this.props.isWhiteboardOpen && (
                        <ScreenShotEditModal visitId={this.props.visitId} />
                    )}
                    <div
                        style={{
                            display: this.props.isWhiteboardOpen ? 'none' : 'contents',
                        }}
                    >
                        {(this.props.role === Doctor || this.props.sessionTypeParams) &&
                            streams &&
                            streams.length > 0 && (
                                <>
                                    {isScreenShared ? (
                                        <>
                                            {/* Match classroom carousel sizing */}
                                            <div
                                                style={{ height: '20%', paddingTop: '4px' }}
                                                className="w-full"
                                            >
                                                <div className="h-full carousel">
                                                    <div className="relative w-full h-full mb-2 overflow-hidden">
                                                        <div className="w-full h-full">
                                                            <div className="relative z-0 flex justify-center h-full gap-1 overflow-hidden carousel-container scroll-smooth snap-x snap-mandatory touch-pan-x">
                                                                {streams.map((stream) => {
                                                                    if (
                                                                        stream.name === 'screen' ||
                                                                        renderedStreams.has(
                                                                            stream.id
                                                                        )
                                                                    ) {
                                                                        return null;
                                                                    }
                                                                    renderedStreams.add(stream.id);
                                                                    return (
                                                                        <div
                                                                            key={stream.id}
                                                                            className="relative text-center carousel-item snap-start"
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    position:
                                                                                        'relative',
                                                                                    width: '264px',
                                                                                    height: '100%',
                                                                                }}
                                                                            >
                                                                                {this.renderSubscriber(
                                                                                    stream,
                                                                                    false
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Main area with shared screen – match classroom */}
                                            <div
                                                style={{ height: '70%', marginBottom: '300px' }}
                                                className="w-full"
                                            >
                                                {streams.map((stream) => {
                                                    if (
                                                        stream.name !== 'screen' ||
                                                        renderedStreams.has(stream.id)
                                                    ) {
                                                        return null;
                                                    }
                                                    renderedStreams.add(stream.id);
                                                    return this.renderSubscriber(stream, false);
                                                })}
                                            </div>
                                        </>
                                    ) : (
                                        // No screen share: keep existing grid layout
                                        streams.map((stream) => {
                                            if (renderedStreams.has(stream.id)) {
                                                return null;
                                            }
                                            renderedStreams.add(stream.id);
                                            return (
                                                <div
                                                    key={stream.id}
                                                    style={{
                                                        position: 'relative',
                                                        width: containerWidth,
                                                        height: containerHeight,
                                                    }}
                                                >
                                                    {this.renderSubscriber(stream, false)}
                                                </div>
                                            );
                                        })
                                    )}
                                </>
                            )}
                    </div>
                    {this.props.role === Doctor &&
                        streams &&
                        streams.map((stream) => {
                            if (renderedStreams.has(stream.id)) {
                                return null;
                            }
                            if (isScreenShared && stream.name === 'screen') {
                                return null; // Already rendered in carousel
                            } else if (shouldHideSmallVideo && stream.name) {
                                renderedStreams.add(stream.id);
                                return (
                                    <div
                                        className={`my-video-screen ${
                                            shouldHideSmallVideo
                                                ? 'opacity-0 pointer-events-none'
                                                : ''
                                        }`}
                                        style={{ zIndex: '1', left: '0', bottom: '0' }}
                                    >
                                        {this.renderSubscriber(stream, true)}
                                    </div>
                                );
                            }
                            return null;
                        })}
                    <div style={{ display: this.props.isWhiteboardOpen ? 'none' : 'block' }}>
                        {(this.props.role === Patient || this.props.role === LevelTwoAdmin) &&
                            !this.props.sessionTypeParams &&
                            streams &&
                            streams.map((stream) => {
                                if (renderedStreams.has(stream.id)) {
                                    return null;
                                }
                                if (isScreenShared && stream.name === 'screen') {
                                    renderedStreams.add(stream.id);
                                    return (
                                        <div className="w-full h-full" style={{ padding: '4px' }}>
                                            {this.renderSubscriber(stream, false)}
                                        </div>
                                    );
                                } else if (!isScreenShared && stream.name === 'Doctor') {
                                    renderedStreams.add(stream.id);
                                    return (
                                        <div
                                            key={stream.id}
                                            style={{
                                                position: 'relative',
                                                width: '100%',
                                                height: '100%',
                                            }}
                                        >
                                            {this.renderSubscriber(stream, false)}
                                        </div>
                                    );
                                }
                                return null;
                            })}
                    </div>
                </div>
                <div style={{ display: this.props.isWhiteboardOpen ? 'none' : 'block' }}>
                    {(this.props.role === Patient || this.props.role === LevelTwoAdmin) &&
                        !this.props.sessionTypeParams &&
                        streams &&
                        streams.map((stream) => {
                            if (renderedStreams.has(stream.id)) {
                                return null;
                            }
                            if (isScreenShared && stream.name === 'Doctor') {
                                renderedStreams.add(stream.id);
                                return (
                                    <div
                                        className={`${
                                            this.props.fullScreenEnabled ? 'bottom-65' : ''
                                        } my-video-screen`}
                                        style={{ zIndex: '1' }}
                                    >
                                        {this.renderSubscriber(stream, true)}
                                    </div>
                                );
                            }
                            return null;
                        })}
                </div>
                {this.renderSubscriberPortals()}
            </div>
        );
    }
}

export default Subscribers;
